import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import { getSetupPrompt, getSetupTemplate, uploadSetupImport } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import "./daily/karkhana-daily.scss";
import "./workers/karkhana-workers.scss";
import "./import/karkhana-import.scss";
import { addedParts, checkedParts, Line, MISSING, Part } from "./import/setupModel";
import PageHeader from "./ui/PageHeader";
import { saveBase64 } from "./ui/saveBase64";
import { useKT } from "./i18n/kt";
import { kp } from "./karkhanaPaths";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

/**
 * One place to bring a karkhana's records into HKP: upload whatever file the studio has (any tabs,
 * any order: people, looms, rates, sarees on looms, advances, old daily work), or let the studio's own
 * AI tidy messy records into HKP's workbook first. Check, then add; the rest can come any time.
 */
const KarkhanaImportPage = () => {
  const { t } = useKT();
  const history = useHistory();
  const role = readRole();
  const canWrite = canManageKarkhana(role) && role !== "SUPER_ADMIN";
  const owner = role === "ORGANIZATION_ADMIN";
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [checked, setChecked] = useState<any>(null);
  const [added, setAdded] = useState<any>(null);
  const [busy, setBusy] = useState<"" | "check" | "apply" | "template" | "prompt">("");
  const [over, setOver] = useState(false);
  const [approve, setApprove] = useState(true);
  const [prompt, setPrompt] = useState("");
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    if (!canManageKarkhana(role)) history.replace(kp.home());
  }, [role, history]);

  // Reasons joined in the model ("no name, in the list twice") are translated one by one.
  const say = (l: Line) => t(l.text, l.vars && typeof l.vars.why === "string" ? { ...l.vars, why: l.vars.why.split(", ").map((w) => t(w)).join(", ") } : l.vars);

  const check = async (f?: File | null) => {
    if (!f || busy) return;
    setBusy("check");
    try {
      const res: any = await uploadSetupImport(f, "check");
      if (!res?.success) {
        notifications({ title: t("Couldn't read the file"), message: t(res?.message || "Something went wrong"), type: "danger" });
        return;
      }
      setFile(f);
      setChecked(res.data);
      setAdded(null);
    } catch {
      notifications({ title: t("No connection"), message: t("Checking a file needs a connection."), type: "warning" });
    } finally {
      setBusy("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const apply = async () => {
    if (!file || busy) return;
    setBusy("apply");
    try {
      const res: any = await uploadSetupImport(file, "apply", owner && approve);
      if (!res?.success) {
        notifications({ title: t("Not added"), message: t(res?.message || "Something went wrong"), type: "danger" });
        return;
      }
      setAdded(res.data);
      window.scrollTo(0, 0);
    } catch {
      notifications({ title: t("No connection"), message: t("Nothing was added. Try again with signal."), type: "warning" });
    } finally {
      setBusy("");
    }
  };

  const template = async () => {
    setBusy("template");
    try {
      const res: any = await getSetupTemplate();
      if (!res?.success) throw new Error(res?.message);
      saveBase64(res.data.fileName || "hkp-karkhana-setup.xlsx", res.data.base64);
    } catch (e: any) {
      notifications({ title: t("Error"), message: t(e?.message || "Could not make the template"), type: "danger" });
    } finally {
      setBusy("");
    }
  };

  const loadPrompt = async (): Promise<string> => {
    if (prompt) return prompt;
    const res: any = await getSetupPrompt();
    if (!res?.success) throw new Error(res?.message);
    setPrompt(res.data.prompt);
    return res.data.prompt;
  };

  const copyPrompt = async () => {
    setBusy("prompt");
    try {
      const text = await loadPrompt();
      try {
        await navigator.clipboard.writeText(text);
        notifications({ title: t("Prompt copied"), message: t("Paste it in ChatGPT or Claude with your files."), type: "success" });
      } catch {
        // Clipboard blocked (some in-app browsers): show it to copy by hand.
        setShowPrompt(true);
      }
    } catch (e: any) {
      notifications({ title: t("Error"), message: t(e?.message || "Could not load the prompt"), type: "danger" });
    } finally {
      setBusy("");
    }
  };

  const startOver = () => {
    setFile(null);
    setChecked(null);
    setAdded(null);
  };

  const parts: Part[] = added ? addedParts(added) : checked ? checkedParts(checked) : [];
  const missing: string[] = (added || checked)?.missing || [];
  const unread: string[] = (added || checked)?.unread || [];
  const hasRates = Number(checked?.rates?.ready) > 0;

  return (
    <div className={`gi-registry-page kw-page kim-page${checked && !added ? " has-foot" : ""}`}>
      <PageHeader
        title={
          file ? (
            <>
              {added ? t("Added to your karkhana") : file.name}
              <small>{added ? file.name : t("Nothing is added until you tap Add")}</small>
            </>
          ) : (
            t("Import")
          )
        }
        back={kp.more()}
      />

      {!checked ? (
        <>
          <div
            className={`kwi-opt is-main${over ? " is-over" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              if (canWrite) check(e.dataTransfer.files?.[0]);
            }}
          >
            <span className="kwi-n">1</span>
            <div>
              <strong>{t("Upload what you have")}</strong>
              <span>{t("Any Excel or CSV: people, looms, rates, sarees on looms, advances, old daily work. One tab or many, any order. Only what you have; add the rest any time.")}</span>
              <label className={`btn ResetBtn btn-sm kwi-pick${!canWrite || busy ? " disabled" : ""}`}>
                {busy === "check" ? t("Reading the file…") : t("Choose file")}
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,text/csv" hidden disabled={!canWrite || !!busy} onChange={(e) => check(e.target.files?.[0])} />
              </label>
            </div>
          </div>

          <div className="kwi-opt kim-ai">
            <span className="kwi-n">2</span>
            <div>
              <strong>{t("Messy or handwritten? Let your AI tidy it")}</strong>
              <ol className="kim-steps">
                <li>{t("Copy HKP's prompt.")}</li>
                <li>{t("Open ChatGPT or Claude, attach your sheets or photos of registers, paste the prompt.")}</li>
                <li>{t("Download the Excel it makes and upload it here.")}</li>
              </ol>
              <div className="kim-row">
                <button type="button" className="btn ResetBtn btn-sm kwi-pick" disabled={busy === "prompt"} onClick={copyPrompt}>
                  {busy === "prompt" ? t("Getting it…") : t("Copy prompt")}
                </button>
                <button
                  type="button"
                  className="btn btn-link btn-sm kim-see"
                  onClick={async () => {
                    if (!showPrompt) await loadPrompt().catch(() => undefined);
                    setShowPrompt(!showPrompt);
                  }}
                  aria-expanded={showPrompt}
                >
                  {showPrompt ? t("Hide prompt") : t("See prompt")}
                </button>
              </div>
              {showPrompt && prompt ? (
                <textarea className="form-control kim-prompt" readOnly rows={10} value={prompt} onFocus={(e) => e.currentTarget.select()} aria-label={t("Prompt")} />
              ) : null}
            </div>
          </div>

          <div className="kwi-opt">
            <span className="kwi-n">3</span>
            <div>
              <strong>{t("Start from HKP's Excel")}</strong>
              <span>{busy === "template" ? t("Making…") : t("A tab for each kind of record, an example row, a Read me in Hindi. Fill what you have.")}</span>
            </div>
            <button type="button" className="kwi-cover" disabled={busy === "template"} onClick={template} aria-label={t("Download template")} />
          </div>

          <div className="kwi-safe">{t("Nothing already in HKP is changed. Give the same file again and nothing is added twice.")}</div>
          <Link to={kp.workerReview()} className="kwi-review">
            {t("People from earlier imports to check")} ›
          </Link>
        </>
      ) : (
        <>
          <ul className="kim-parts">
            {parts.map((p) => (
              <li key={p.key} className={`kim-part${p.warn ? " is-warn" : ""}`}>
                <div className="kim-part__head">
                  <strong>{t(p.title)}</strong>
                  <span>{say(p.line)}</span>
                </div>
                {p.from ? <small className="kim-part__from">{t("from “{{tab}}”", { tab: p.from })}</small> : null}
                {p.notes.length ? (
                  <ul className="kim-part__notes">
                    {p.notes.map((n, i) => (
                      <li key={i}>{say(n)}</li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>

          {missing.length ? (
            <div className="kim-later">
              <strong>{added ? t("Still to add, whenever you're ready") : t("Not in this file. Fine, add later")}</strong>
              <ul>
                {missing.map((m) => (MISSING[m] ? <li key={m}>{say(MISSING[m])}</li> : null))}
              </ul>
            </div>
          ) : null}
          {unread.length ? <p className="kwi-note">{t("Tabs not read: {{tabs}}", { tabs: unread.join(", ") })}</p> : null}

          {added ? (
            <div className="kim-next">
              <Link className="kd-btn kd-btn--primary" to={kp.dailyWork()}>
                {t("Go to Daily work")}
              </Link>
              {added.people?.toCheck ? (
                <Link className="kd-btn kd-btn--line" to={kp.workerReview()}>
                  {t("Check {{n}} people", { n: added.people.toCheck })}
                </Link>
              ) : null}
              {missing.includes("RATES") ? (
                <Link className="kd-btn kd-btn--line" to={kp.pay(undefined, "not-counted")}>
                  {t("Fill rates later")}
                </Link>
              ) : (
                <Link className="kd-btn kd-btn--line" to={kp.rateCards()}>
                  {t("See rate cards")}
                </Link>
              )}
              {added.dailyWork?.problems ? (
                <Link className="kd-btn kd-btn--line" to={kp.oldSheet()}>
                  {t("Look at old daily work")}
                </Link>
              ) : null}
              <button type="button" className="btn btn-link kwi-again" onClick={startOver}>
                {t("Import another file")}
              </button>
            </div>
          ) : (
            <>
              {hasRates && owner ? (
                <label className="kim-approve">
                  <input type="checkbox" checked={approve} onChange={(e) => setApprove(e.target.checked)} />
                  <span>{t("Approve the rates in this file (they are paid from their start date)")}</span>
                </label>
              ) : null}
              <button type="button" className="btn btn-link kwi-again" onClick={startOver}>
                {t("Choose another file")}
              </button>
              <div className="kf-foot">
                <button type="button" className="kd-btn kd-btn--primary" disabled={!canWrite || busy === "apply" || !parts.length} onClick={apply}>
                  {busy === "apply" ? t("Adding…") : t("Add to my karkhana")}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default KarkhanaImportPage;
